import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { getQTipData } from '../Tooltip/qTip.utilities';
import { overlayStyles } from '../styles/overlays';
import { colors } from '../theme/colors.stylex';
import type { ButtonWithPopoverProps } from './ButtonWithPopover.types';

// oxlint-disable-next-line no-undef -- replaced by the bundler, as with the shared tooltip delay
const DEFAULT_HOVER_OPEN_DELAY = process.env.NODE_ENV === 'test' ? 15 : 300;

const styles = stylex.create({
  trigger: {
    backgroundColor: 'transparent',
    outlineStyle: 'none',
    borderWidth: 0,
    padding: 0,
    font: 'inherit',
    color: 'inherit',
  },
  fullSize: { height: '100%', width: '100%' },
  triggerContent: { outlineStyle: 'none', color: colors.menuText },
  fullWidthContent: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
  inline: { display: 'inline' },
  disabled: { cursor: 'not-allowed', opacity: 0.5 },
  content: {
    position: 'relative',
    zIndex: 1200,
    minWidth: '1.5rem',
    width: '100%',
    height: '100%',
    color: colors.text,
  },
});

const ButtonWithPopover = ({
  children,
  trigger,
  id,
  hasArrow,
  extraTriggerClassNames,
  extraPopoverClassNames,
  xstyle,
  contentXstyle,
  containerTestId,
  onInteractOutside,
  onPointerDownOutside,
  isTriggerAsChild = false,
  shouldTriggerFullWidth = false,
  disabled = false,
  align = 'end',
  alignOffset = -35,
  placement = 'bottom',
  placementOffset = 2,
  isHoverEnabled = false,
  hoverOpenDelay = DEFAULT_HOVER_OPEN_DELAY,
  onOpenChange,
  isOpen,
  isCloseOnContentClick = false,
  isPortal = false,
  hideWhenDetached = false,
  ...tooltipProps
}: ButtonWithPopoverProps) => {
  const tooltipData = getQTipData(tooltipProps);
  const timeout = useRef<null | NodeJS.Timeout>(null);
  const [isHoveredOpen, setIsHoveredOpen] = useState<boolean>(false);
  const clearHoverTimeout = () => {
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }
  };

  const onHoverStart = () => {
    if (isHoverEnabled) {
      clearHoverTimeout();
      timeout.current = setTimeout(() => {
        setIsHoveredOpen(true);
        onOpenChange && onOpenChange(true);
      }, hoverOpenDelay);
    }
  };

  const onHoverEnd = () => {
    if (isHoverEnabled) {
      clearHoverTimeout();
      timeout.current = setTimeout(() => {
        setIsHoveredOpen(false);
        onOpenChange && onOpenChange(false);
      }, 200);
    }
  };

  useEffect(() => clearHoverTimeout, []);

  const contentProps = stylex.props(overlayStyles.panel, overlayStyles.motion, styles.content, contentXstyle);
  const triggerContentProps = stylex.props(
    styles.triggerContent,
    disabled && styles.disabled,
    shouldTriggerFullWidth ? styles.fullWidthContent : styles.inline,
    xstyle,
  );
  const renderContent = () => {
    return (
      <PopoverPrimitive.Content
        sideOffset={placementOffset}
        align={align}
        side={placement}
        onInteractOutside={onInteractOutside}
        onPointerDownOutside={onPointerDownOutside}
        alignOffset={isHoverEnabled ? -10 : alignOffset}
        hideWhenDetached={hideWhenDetached}
        asChild
        onClick={() => isCloseOnContentClick && onOpenChange && onOpenChange(false)}>
        <div
          data-testid={containerTestId}
          {...contentProps}
          className={clsx(contentProps.className, extraPopoverClassNames)}>
          {hasArrow && (
            <PopoverPrimitive.Arrow asChild>
              <div {...stylex.props(overlayStyles.arrow)} />
            </PopoverPrimitive.Arrow>
          )}
          {children}
        </div>
      </PopoverPrimitive.Content>
    );
  };

  const renderPopover = (popoverOpenState?: boolean) => {
    return (
      <PopoverPrimitive.Root open={popoverOpenState} defaultOpen={false} onOpenChange={onOpenChange}>
        {trigger ? (
          <PopoverPrimitive.Trigger
            id={id}
            asChild={isTriggerAsChild}
            {...stylex.props(styles.trigger, shouldTriggerFullWidth && styles.fullSize)}
            disabled={disabled}>
            <div
              {...tooltipData}
              onMouseOver={onHoverStart}
              onMouseOut={onHoverEnd}
              {...triggerContentProps}
              className={clsx(triggerContentProps.className, extraTriggerClassNames)}>
              {trigger}
            </div>
          </PopoverPrimitive.Trigger>
        ) : (
          <PopoverPrimitive.Trigger asChild>
            <div />
          </PopoverPrimitive.Trigger>
        )}
        {isPortal ? <PopoverPrimitive.Portal>{renderContent()}</PopoverPrimitive.Portal> : renderContent()}
      </PopoverPrimitive.Root>
    );
  };

  return isHoverEnabled ? renderPopover(isHoveredOpen) : renderPopover(isOpen);
};

export default ButtonWithPopover;
